import { Formula } from './ai-math.jsx';
import { splitDisplayMath } from './ai-math-syntax.mjs';
import { splitUI } from './ai-ui-protocol.cjs';
import { UIPanel } from './ai-ui.jsx';
import { StreamingTextContext } from './ai-stream.jsx';
import { InlineMarkdown as Inline } from './ai-inline.jsx';
export { safeLink } from './ai-inline.jsx';

// Model output is rendered as text/React nodes, never injected as HTML.
function TextMarkdown({ text, sources, onCitation }) {
  const lines = text.split('\n'), output = [];
  const inline = value => <Inline text={value} sources={sources} onCitation={onCitation}/>;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i], key = i;
    if (!line.trim()) continue;
    const fence = line.match(/^[ \t]{0,3}(`{3,}|~{3,})/);
    if (fence) {
      const close = new RegExp('^[ \\t]{0,3}' + fence[1][0] + '{' + fence[1].length + ',}[ \\t]*\\r?$');
      const code = []; while (++i < lines.length && !close.test(lines[i])) code.push(lines[i]);
      output.push(<pre key={key}><code>{code.join('\n')}</code></pre>); continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) { const H = heading[1].length <= 2 ? 'h2' : 'h3'; output.push(<H key={key}>{inline(heading[2])}</H>); continue; }
    if (line.includes('|') && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1] || '')) {
      const split = row => row.trim().replace(/^\||\|$/g, '').split('|').map(s => s.trim());
      const header = split(line), rows = []; i += 2;
      for (; i < lines.length && lines[i].includes('|') && lines[i].trim(); i++) rows.push(split(lines[i]));
      i--;
      output.push(<div className="ai-table-wrap" key={key}><table><thead><tr>{header.map((cell, j) => <th key={j}>{inline(cell)}</th>)}</tr></thead><tbody>{rows.map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k}>{inline(cell)}</td>)}</tr>)}</tbody></table></div>); continue;
    }
    if (/^\s*(?:[-*+] |\d+[.)] )/.test(line)) {
      const ordered = /^\s*\d/.test(line), items = [];
      while (i < lines.length && (ordered ? /^\s*\d+[.)] / : /^\s*[-*+] /).test(lines[i])) {
        items.push(<li key={i}>{inline(lines[i].replace(/^\s*(?:[-*+] |\d+[.)] )/, ''))}</li>); i++;
      }
      i--; const List = ordered ? 'ol' : 'ul'; output.push(<List key={key}>{items}</List>); continue;
    }
    if (/^>\s?/.test(line)) { output.push(<blockquote key={key}>{inline(line.replace(/^>\s?/, ''))}</blockquote>); continue; }
    if (/^---+$/.test(line.trim())) { output.push(<hr key={key}/>); continue; }
    const paragraph = [line];
    while (i + 1 < lines.length && lines[i + 1].trim() && !/^(?:#{1,4} |[ \t]{0,3}(?:`{3,}|~{3,})|> |\s*[-*+] |\s*\d+[.)] )/.test(lines[i + 1]) && !lines[i + 1].includes('|')) paragraph.push(lines[++i]);
    output.push(<p key={key}>{inline(paragraph.join('\n'))}</p>);
  }
  return output;
}

export function Markdown({ text, sources, onCitation }) {
  const cache = React.useRef(new Map()), streaming = React.useContext(StreamingTextContext);
  return splitUI(text, cache.current, { final: !streaming }).map((block, key) => block.type === 'ui' ? <UIPanel key={block.key} part={block} sources={sources} onCitation={onCitation}/>
    : block.type === 'pending-ui' ? <div className="qui-pending" key={'pending-' + key} role="status">{streaming && <span/>}{streaming ? '正在构建交互组件…' : '此组件未完整生成，可重新搜索。'}</div>
    : <React.Fragment key={'text-' + key}>{splitDisplayMath(block.text).map((part, index) => part.type === 'math'
    ? <Formula key={index} tex={part.text} display/>
    : part.type === 'pending'
      ? <div className="ai-formula-pending" key={index} role="status">{streaming ? '公式正在排版…' : '此公式未完整生成，可重新搜索。'}</div>
      : <TextMarkdown key={index} text={part.text} sources={sources} onCitation={onCitation}/>)}</React.Fragment>);
}
